/**
 * Demo TemplateDocuments for public-share fixtures.
 * Real V2 shape (same as Containd seeds) — not Hollywood card color blobs.
 */
import type { TemplateDocument } from '../vendor/template-system/v2/types/canonical';
import {
  V2_SMOKE_TEST_TEMPLATE,
  V2_SMOKE_TEST_TEMPLATE_ID,
  V2_SMOKE_TEST_ELEMENT_ID,
} from '../vendor/template-system/seeds/V2SmokeTestTemplate';

const PREVIEW = {
  kind: 'inline' as const,
  mediaType: 'image/png',
  data: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==',
};

/** Multi-layout item card used by cookware / pantry / camping items. */
export const DEMO_ITEM_TEMPLATE_ID = 'tpl-demo-item';
export const DEMO_ITEM_ELEMENT_ID = 'el-demo-item';

export const DEMO_ITEM_TEMPLATE: TemplateDocument = {
  kind: 'template',
  version: 2,
  metadata: {
    templateId: DEMO_ITEM_TEMPLATE_ID,
    displayName: { en: 'Demo Item' },
    description: { en: 'Multi-layout item template for public share fixtures.' },
    primaryElementId: DEMO_ITEM_ELEMENT_ID,
    preview: PREVIEW,
    previewThumb: PREVIEW,
  },
  elements: [
    {
      id: DEMO_ITEM_ELEMENT_ID,
      displayName: { en: 'Item Card' },
      categoryIds: [],
      preview: PREVIEW,
      layouts: [
        {
          id: 'layout-card',
          displayName: { en: 'Card' },
          isDefault: true,
          components: [
            {
              id: 'node-title',
              type: 'text',
              layout: { position: 'absolute', x: 0.06, y: 0.08, width: 0.88, height: 0.28 },
              text: {
                source: { kind: 'binding', ref: { kind: 'itemField', field: 'title' } },
                editable: false,
              },
            },
            {
              id: 'node-amount',
              type: 'number',
              layout: { position: 'absolute', x: 0.06, y: 0.42, width: 0.4, height: 0.18 },
              number: {
                source: { kind: 'binding', ref: { kind: 'itemField', field: 'amount' } },
                editable: false,
              },
            },
            {
              id: 'node-desc',
              type: 'text',
              layout: { position: 'absolute', x: 0.06, y: 0.64, width: 0.88, height: 0.28 },
              text: {
                source: { kind: 'binding', ref: { kind: 'itemField', field: 'description' } },
                editable: false,
              },
            },
          ],
        },
        {
          id: 'layout-compact',
          displayName: { en: 'Compact' },
          components: [
            {
              id: 'node-title-c',
              type: 'text',
              layout: { position: 'absolute', x: 0.06, y: 0.35, width: 0.88, height: 0.3 },
              text: {
                source: { kind: 'binding', ref: { kind: 'itemField', field: 'title' } },
                editable: false,
              },
            },
          ],
        },
        {
          id: 'layout-list',
          displayName: { en: 'List' },
          components: [
            {
              id: 'node-title-l',
              type: 'text',
              layout: { position: 'absolute', x: 0.06, y: 0.15, width: 0.88, height: 0.35 },
              text: {
                source: { kind: 'binding', ref: { kind: 'itemField', field: 'title' } },
                editable: false,
              },
            },
            {
              id: 'node-desc-l',
              type: 'text',
              layout: { position: 'absolute', x: 0.06, y: 0.55, width: 0.88, height: 0.3 },
              text: {
                source: { kind: 'binding', ref: { kind: 'itemField', field: 'description' } },
                editable: false,
              },
            },
          ],
        },
      ],
      overview: {
        recipeId: 'productHero',
        columnSplit: 'auto',
        hero: { source: { kind: 'itemImage', preferredIndex: 0 }, fit: 'cover', size: 'md' },
        slots: {
          subtitle: {
            source: { kind: 'binding', ref: { kind: 'itemField', field: 'description' } },
            label: { en: 'Description' },
          },
          highlight: {
            source: { kind: 'binding', ref: { kind: 'itemField', field: 'amount' } },
            label: { en: 'Qty' },
          },
        },
      },
      interactions: { selectedTapAction: 'auto' },
      itemDefaults: {
        itemFields: {
          title: 'Demo Item',
          description: '',
          amount: 1,
        },
      },
    },
  ],
  categories: [],
};

/** Container template (2 layouts) for nestable folders. */
export const DEMO_CONTAINER_TEMPLATE_ID = 'tpl-demo-container';
export const DEMO_CONTAINER_ELEMENT_ID = 'el-demo-container';

export const DEMO_CONTAINER_TEMPLATE: TemplateDocument = {
  kind: 'template',
  version: 2,
  metadata: {
    templateId: DEMO_CONTAINER_TEMPLATE_ID,
    displayName: { en: 'Demo Container' },
    description: { en: 'Container tile for nested canvases.' },
    primaryElementId: DEMO_CONTAINER_ELEMENT_ID,
    preview: PREVIEW,
    previewThumb: PREVIEW,
  },
  elements: [
    {
      id: DEMO_CONTAINER_ELEMENT_ID,
      displayName: { en: 'Container' },
      categoryIds: [],
      preview: PREVIEW,
      layouts: [
        {
          id: 'layout-folder',
          displayName: { en: 'Folder' },
          isDefault: true,
          components: [
            {
              id: 'node-badge',
              type: 'text',
              layout: { position: 'absolute', x: 0.06, y: 0.08, width: 0.4, height: 0.16 },
              text: {
                source: { kind: 'static', value: 'FOLDER' },
                editable: false,
              },
            },
            {
              id: 'node-title',
              type: 'text',
              layout: { position: 'absolute', x: 0.06, y: 0.32, width: 0.88, height: 0.4 },
              text: {
                source: { kind: 'binding', ref: { kind: 'itemField', field: 'title' } },
                editable: false,
              },
            },
          ],
        },
        {
          id: 'layout-compact',
          displayName: { en: 'Compact' },
          components: [
            {
              id: 'node-title-c',
              type: 'text',
              layout: { position: 'absolute', x: 0.08, y: 0.35, width: 0.84, height: 0.3 },
              text: {
                source: { kind: 'binding', ref: { kind: 'itemField', field: 'title' } },
                editable: false,
              },
            },
          ],
        },
      ],
      interactions: { selectedTapAction: 'auto' },
      itemDefaults: {
        itemFields: { title: 'Container', description: '', amount: 0 },
      },
    },
  ],
  categories: [],
};

export const FIXTURE_TEMPLATE_DOCUMENTS: Record<string, TemplateDocument> = {
  [DEMO_ITEM_TEMPLATE_ID]: DEMO_ITEM_TEMPLATE,
  [DEMO_CONTAINER_TEMPLATE_ID]: DEMO_CONTAINER_TEMPLATE,
  [V2_SMOKE_TEST_TEMPLATE_ID]: V2_SMOKE_TEST_TEMPLATE,
};

export {
  V2_SMOKE_TEST_TEMPLATE,
  V2_SMOKE_TEST_TEMPLATE_ID,
  V2_SMOKE_TEST_ELEMENT_ID,
};
